
import { useEffect, useRef, useState } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { FaApple, FaGooglePlay, FaArrowRight, FaUser } from "react-icons/fa";
import { CREATE_GAME_TAB, DAILY_GAMES_TAB } from "@chesshotel/constants";
import { lobbyStore, setTab } from "@/redux/lobby";
import { loginGuest, useUser } from "@/redux/user";
import { useSiteData } from "@/redux/siteData";
import { useSignIn } from "@/redux/signIn";
import { availableGuestName } from "@/util/guestNames";
import { readGuestIdentity } from "@/util/guestIdentity";
import { hasBadWord } from "@/util/badWords";
import { changeGuestName } from "@/services/guestName";
import { trackEvent } from "@/services/eventTracker";
import { useMetrica } from "@/lib/next-yandex-metrica";
import Spinner from "@/components/ui/spinner";
import { buttonVariants } from "@/components/ui/button";
import { IPHONE_APP_URL, ANDROID_APP_URL } from "@/services/appDownload";
import { USE_PROD } from "@/services/runtimeHost";
import styles from "@/pages/styles/Welcome.module.css";

const FrontPagePuzzle = dynamic(() => import("@/modules/puzzle/FrontPagePuzzle"), { ssr: false });
const APP_QR_IMAGE = process.env.NEXT_PUBLIC_TEST === "true" ? "/images/chesshotel-app-qr-test.svg" : "/images/chesshotel-app-qr.svg";

export default function NewHome() {
  const { t } = useTranslation("common");
  const { t: homeT } = useTranslation("home");
  const { t: authT } = useTranslation("auth");
  const router = useRouter();
  const { reachGoal } = useMetrica();
  const { playersOnline } = useSiteData();
  const { loading } = useSignIn();
  const { isGuest, displayName, isDefaultGuest, memberLoggedIn } = useUser();
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPuzzle, setShowPuzzle] = useState(false);
  const [twoPhones, setTwoPhones] = useState(true);
  const puzzleSection = useRef<HTMLElement>(null);
  const nameEdited = useRef(false);
  const homepageTracked = useRef(false);
  const returningGuest = useRef(!!readGuestIdentity());
  const primaryAction = { ready: true, value: true };

  // The treatment defaults to two phones; only local development can preview one.
  useEffect(() => {
    setTwoPhones(!(process.env.NODE_ENV === "development" && !USE_PROD && router.query?.appPreview === "single-phone"));
  }, [router.query?.appPreview]);

  useEffect(() => {
    if (router.isReady && memberLoggedIn) void router.replace("/play");
  }, [router.isReady, memberLoggedIn, router]);
  useEffect(() => {
    if (loading || !isGuest || homepageTracked.current) return;
    homepageTracked.current = true;
    void trackEvent("homepage_viewed", { flowVersion: 2, returningGuest: returningGuest.current });
  }, [loading, isGuest]);
  useEffect(() => {
    const saved = readGuestIdentity();
    if (saved?.chosen && !nameEdited.current) setUsername(saved.name);
  }, []);
  useEffect(() => {
    if (isGuest && displayName && !nameEdited.current) setUsername(displayName);
  }, [isGuest, displayName]);
  useEffect(() => {
    if (!puzzleSection.current) return;
    if (typeof IntersectionObserver === "undefined") { setShowPuzzle(true); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setShowPuzzle(true); observer.disconnect(); }
    }, { rootMargin: "200px" });
    observer.observe(puzzleSection.current);
    return () => observer.disconnect();
  }, [loading, memberLoggedIn]);

  async function play(entrySource = "hero") {
    if (busy || loading || !primaryAction.ready) return;
    const name = username.trim();
    if (name && (name.length > 18 || hasBadWord(name) || /[\u0000-\u001f\u007f]/.test(name))) {
      setError(homeT("welcome.guestError"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (!isGuest) await loginGuest(name || await availableGuestName(), { generated: !name, chosen: !!name });
      else if (name && name !== displayName) {
        const failure = await changeGuestName({ name, generated: false, chosen: true });
        if (failure) { setError(t(`guestIdentity.error.${failure}`)); return; }
      }
      void trackEvent("homepage_play_selected", { entrySource, flowVersion: 2, guestPrimary: primaryAction.value, customName: !!name && (nameEdited.current || !isDefaultGuest) });
      if (window.innerWidth <= 768 && lobbyStore.getState().tab !== DAILY_GAMES_TAB) setTab(CREATE_GAME_TAB);
      await router.push("/play");
    } finally { setBusy(false); }
  }

  // Wait for the saved session before deciding whether to show the guest homepage.
  if (loading || memberLoggedIn) return <div role="status" aria-label={t("dailyPractice.loading")} className="flex justify-center p-10"><Spinner blue={false} /></div>;

  return <>
    <Head>
      <title>{`Chess Hotel | ${homeT("Play chess online")}`}</title>
      <meta name="description" content={t("description")} />
    </Head>
    <main dir={["ar", "he", "fa", "ps"].includes(router.locale || "") ? "rtl" : "ltr"}>
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        <h1>{homeT("welcome.title")}</h1>
        <p className={styles.subtitle}>{homeT("welcome.subtitle")}</p>
        {!primaryAction.value && <Link href="/register" className={`${styles.primaryButton} ${styles.signupPrimary}`} onClick={() => { reachGoal("signup_start_homepage_createaccount_button"); void trackEvent("homepage_signup_selected", { flowVersion: 2 }); }}>{homeT("welcome.account")}<FaArrowRight aria-hidden="true" /></Link>}
        <form className={styles.playForm} onSubmit={(event) => { event.preventDefault(); void play(); }}>
          {playersOnline > 0 && <div className={styles.onlineAbove}><span className={styles.online}><span className={styles.onlineDot} /><FaUser aria-hidden="true" />{homeT("welcome.online", { count: playersOnline })}</span></div>}
          <div className={styles.playRow}>
            <label className={styles.guestInput} htmlFor="homepage-guest-name">
              <input id="homepage-guest-name" aria-label={authT("Username")} autoComplete="nickname" maxLength={18} value={username} placeholder={authT("Username")} onChange={(event) => { nameEdited.current = true; setUsername(event.target.value); setError(""); }} aria-invalid={!!error} aria-describedby={error ? "homepage-guest-error" : undefined} />
            </label>
            <button className={primaryAction.value ? styles.primaryButton : styles.secondaryButton} type="submit" disabled={busy || loading || !primaryAction.ready}>{homeT("welcome.play")}<FaArrowRight aria-hidden="true" /></button>
          </div>
          {error && <p id="homepage-guest-error" role="alert" className={styles.error}>{error}</p>}
        </form>
        <div className={styles.heroMeta}>
          {playersOnline > 0 && <span className={`${styles.online} ${styles.onlineBelow}`}><span className={styles.onlineDot} /><FaUser aria-hidden="true" />{homeT("welcome.online", { count: playersOnline })}</span>}
          <div className={styles.accountLinks}><Link href="/register" onClick={() => { reachGoal("signup_start_homepage_createaccount_button"); void trackEvent("homepage_signup_selected", { entrySource: "hero", flowVersion: 2 }); }}>{homeT("welcome.account")}</Link></div>
        </div>
      </div>
    </section>
    <section ref={puzzleSection} className={styles.puzzleSection} aria-label={homeT("welcome.puzzleTitle")}>
      {showPuzzle ? <FrontPagePuzzle onPlay={() => { void play("puzzle"); }} playDisabled={busy || loading || !primaryAction.ready} /> : <div className={styles.puzzlePlaceholder}><h2>{homeT("welcome.puzzleTitle")}</h2><p>{homeT("welcome.puzzleLoading")}</p><Link href="/puzzle" className={buttonVariants({ variant: "quietFilled", className: "gap-2 text-sm" })}>{homeT("welcome.morePuzzles")}<FaArrowRight aria-hidden="true" /></Link></div>}
    </section>
    <section id="mobile-app" className={`${styles.appSection} ${twoPhones ? styles.appSectionPair : ""}`}>
      <div className={styles.appInner}>
        <div className={styles.appCopy}>
          <h2>{homeT("welcome.appTitle")}</h2>
          <p>{homeT("welcome.appText")}</p>
          <div className={styles.appDownloads}>
            <div className={styles.appLinks}>
              <a className={styles.appStore} href={IPHONE_APP_URL} aria-label={homeT("welcome.appDownload")} onClick={() => { void trackEvent("homepage_app_selected", { platform: "iphone" }); }}><FaApple className="text-2xl" aria-hidden="true" /><span>{homeT("welcome.appDownload")}</span></a>
              <a className={styles.appStore} href={ANDROID_APP_URL} onClick={() => { void trackEvent("homepage_app_selected", { platform: "android" }); }}><FaGooglePlay className="text-2xl" aria-hidden="true" /><span>{homeT("welcome.androidDownload")}</span></a>
            </div>
            <div className={styles.qr}><Link href="/app" aria-label={homeT("welcome.qrAlt")}><Image src={APP_QR_IMAGE} alt="" width={88} height={88} /></Link><span>{homeT("welcome.qrText")}</span></div>
          </div>
        </div>
        {twoPhones
          ? <div className={styles.phonePair} role="img" aria-label={homeT("welcome.appPreview")}>
              <div className={`${styles.pairDevice} ${styles.pairBack}`} aria-hidden="true"><Image src="/images/iphone-current-live.png" alt="" width={1320} height={2868} sizes="(max-width: 767px) 166px, 205px" /></div>
              <div className={`${styles.pairDevice} ${styles.pairFront}`} aria-hidden="true"><Image src="/images/iphone-current-auth.png" alt="" width={1320} height={2868} sizes="(max-width: 767px) 166px, 205px" /></div>
            </div>
          : <div className={styles.phone}><Image src="/images/iphone-play-clean.png" alt={homeT("welcome.appPreview")} width={850} height={1850} sizes="(max-width: 767px) 220px, 248px" /></div>}
      </div>
    </section>
    <section className={styles.closing}><h2>{homeT("welcome.closingTitle")}</h2><button type="button" className={styles.primaryButton} disabled={busy || loading || !primaryAction.ready} onClick={() => { void play("closing"); }}>{homeT("welcome.play")}<FaArrowRight aria-hidden="true" /></button></section>
    </main>
  </>;
}
